import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Dialog from "../dialog/Dialog";
import ImageInput from "./ImageInput";
import FilePreview from "../FilePreview/FilePreview";

export const slugifyCategoryName = (name) => {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return base ? `${base}-${Date.now()}` : `category-${Date.now()}`;
};

const InlineCategoryCreateDialog = ({
  open,
  onClose,
  onCreated,
  createFn,
  queryKey,
  requiresImage = false,
  title = "Add Category",
  nameLabel = "Category name",
  namePlaceholder = "Enter category name",
  emptyNameError = "Category name is required.",
  createErrorMessage = "Failed to create category.",
}) => {
  const [name, setName] = useState("");
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  const reset = () => {
    setName("");
    setImage(null);
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: createFn,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey });
      const payload = response?.data?.data ?? response?.data ?? response;
      const newId = payload?.id;
      if (newId) {
        onCreated?.(newId);
      }
      handleClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || createErrorMessage);
    },
  });

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(emptyNameError);
      return;
    }
    if (requiresImage && !image) {
      setError("Category image is required.");
      return;
    }
    setError("");
    mutation.mutate({ name: trimmed, image });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      width="w-[520px]"
      closeButtonText="Cancel"
      primaryButtonText="Save"
      primaryButtonAction={handleSubmit}
      isLoading={mutation.isPending}
      isPrimaryDisabled={!name.trim() || (requiresImage && !image)}
    >
      <div className="space-y-4 p-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {nameLabel} <span className="text-red-500">*</span>
          </label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
            placeholder={namePlaceholder}
          />
        </div>

        {requiresImage && (
          <div className="space-y-2">
            <ImageInput
              onChange={(file) => {
                setImage(file);
                setError("");
              }}
              multiple={false}
              showPreview={false}
              label="Category image"
              required
            />
            {image && (
              <FilePreview
                file={image}
                label="Category image"
                onDelete={() => setImage(null)}
                isImage={true}
              />
            )}
          </div>
        )}

        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    </Dialog>
  );
};

export default InlineCategoryCreateDialog;
